import { useState } from 'react';
import { parseApiError } from '@/lib/api-error';

const API_URL = import.meta.env.VITE_API_URL ?? '';

export interface Candidate {
  wabaId: string;
  phoneNumberId: string;
  displayPhoneNumber: string;
  wabaName: string;
}

interface ConnectPickerProps {
  candidates: Candidate[];
  stateId: string;
  onError: (msg: string) => void;
}

/**
 * Public (account-less) multi-WABA picker.
 *
 * Rendered by ConnectCallbackPage when `POST /org/connect/finalize` returns
 * `status: 'selection_required'`. Lists the candidate phones; on pick, POSTs
 * `POST /org/connect/select` with the chosen (stateId, wabaId, phoneNumberId)
 * and navigates the browser to the returned `redirectUrl`.
 */
export function ConnectPicker({ candidates, stateId, onError }: ConnectPickerProps) {
  const [submitting, setSubmitting] = useState(false);

  const handleSelect = async (candidate: Candidate) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/org/connect/select`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          stateId,
          wabaId: candidate.wabaId,
          phoneNumberId: candidate.phoneNumberId,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        const apiError = parseApiError(
          res.status,
          data,
          'Something went wrong. Please try the link again.',
        );
        onError(apiError.userMessage);
        return;
      }
      const data = (await res.json()) as { redirectUrl?: string };
      if (data.redirectUrl) {
        window.location.href = data.redirectUrl;
      }
    } catch {
      onError('Connection error. Please try the link again.');
    } finally {
      setSubmitting(false);
    }
  };

  const containerStyle: React.CSSProperties = {
    maxWidth: 480,
    width: '100%',
    padding: '0 16px',
  };

  const titleStyle: React.CSSProperties = {
    fontSize: 20,
    fontWeight: 600,
    marginBottom: 8,
  };

  const subtitleStyle: React.CSSProperties = {
    color: '#555',
    marginBottom: 24,
    fontSize: 14,
  };

  const listStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    listStyle: 'none',
    padding: 0,
    margin: 0,
  };

  const buttonStyle = (disabled: boolean): React.CSSProperties => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    width: '100%',
    padding: '14px 18px',
    border: '1.5px solid #e0e0e0',
    borderRadius: 12,
    background: disabled ? '#f8f8f8' : '#fff',
    cursor: disabled ? 'not-allowed' : 'pointer',
    textAlign: 'left',
    transition: 'border-color 0.15s, box-shadow 0.15s',
    opacity: disabled ? 0.7 : 1,
  });

  const wabaNameStyle: React.CSSProperties = {
    fontWeight: 600,
    fontSize: 15,
    color: '#1C1E21',
    marginBottom: 2,
  };

  const phoneStyle: React.CSSProperties = {
    fontSize: 13,
    color: '#666',
  };

  return (
    <div style={containerStyle}>
      <h1 style={titleStyle}>Choose a WhatsApp number</h1>
      <p style={subtitleStyle}>
        Select which number you would like to connect.
      </p>
      <ul style={listStyle}>
        {candidates.map((c) => (
          <li key={`${c.wabaId}-${c.phoneNumberId}`}>
            <button
              type="button"
              aria-label={`Connect ${c.displayPhoneNumber} from ${c.wabaName}`}
              disabled={submitting}
              style={buttonStyle(submitting)}
              onClick={() => void handleSelect(c)}
            >
              <span style={wabaNameStyle}>{c.wabaName}</span>
              <span style={phoneStyle}>{c.displayPhoneNumber}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
